import type { Paginated } from './api';
import type { FileMimeType } from './file';
import type { FormQuestionInput, FormSectionInput } from './form-question';

export const EVENT_CATEGORIES = [
  'Open house',
  'Open day',
  'Workshop',
  'Networking',
  'Roundtable',
  'Studio clinic',
] as const;
export type EventCategory = (typeof EVENT_CATEGORIES)[number];

export const EVENT_SPACES = [
  'Coworking floor',
  'Café Hall · 60 seats',
  'Training Room · 20 seats',
  'Small Meeting Room · 6 seats',
  'Creative Studio · 8 seats',
] as const;
export type EventSpace = (typeof EVENT_SPACES)[number];

export interface EventAgendaItem {
  startMinute: number | null;
  description: string;
}

export interface EventInformationItem {
  label: string;
  value: string;
}

export interface EventMediaItem {
  fileId: string;
  originalFilename: string;
  mimeType: Extract<FileMimeType, `image/${string}`>;
  contentUrl: string;
  displayOrder: number;
  isCover: boolean;
}

/**
 * Where an event's post-event feedback stands, as the programme lists it.
 * The form itself is read and edited through the feedback endpoints.
 */
export interface AdminEventFeedbackState {
  /** Invitations go out to checked-in guests once the event ends. */
  isEnabled: boolean;
  /** At least one invitation has been queued, so the form can no longer change. */
  locked: boolean;
  invitations: number;
  responses: number;
}

export interface AdminEvent {
  id: string;
  slug: string;
  title: string;
  category: EventCategory;
  eventDate: string;
  startMinute: number;
  endMinute: number;
  space: EventSpace;
  capacity: number;
  registeredSeats: number;
  status: 'published' | 'full';
  price: {
    amountMinor: number;
    currency: 'USD';
  };
  shortDescription: string;
  longDescription: string;
  priceNote: string;
  agenda: EventAgendaItem[];
  goodToKnow: EventInformationItem[];
  cover: EventMediaItem | null;
  media: EventMediaItem[];
  feedback: AdminEventFeedbackState;
  publishedAt: string;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminEventRequest {
  title: string;
  category: EventCategory;
  eventDate: string;
  startMinute: number;
  endMinute: number;
  space: EventSpace;
  capacity: number;
  priceAmountMinor?: number;
  shortDescription: string;
  longDescription?: string;
  priceNote?: string;
  agenda?: EventAgendaItem[];
  goodToKnow?: EventInformationItem[];
  mediaFileIds: string[];
  /**
   * The registration form the event starts with, written as its version 1. Left out, the event
   * gets the default questions. Only creation takes it; afterwards the form has its own endpoint.
   */
  registrationForm?: {
    ungroupedQuestions?: FormQuestionInput[];
    sections?: FormSectionInput[];
  };
  /**
   * The post-event feedback form the event starts with, written as its version 1. Left out, the
   * event has no form until one is saved from the event page. Only creation takes it.
   */
  feedbackForm?: {
    isEnabled: boolean;
    ungroupedQuestions?: FormQuestionInput[];
    sections?: FormSectionInput[];
  };
}

export type UpdateAdminEventRequest = Partial<
  Omit<CreateAdminEventRequest, 'registrationForm' | 'feedbackForm'>
>;

export type AdminEventListResponse = Paginated<AdminEvent>;
export interface AdminEventResponse {
  data: AdminEvent;
}
